"use client";

import React, { useEffect, useRef, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  title: "Talent Discovery",
  description: "We scout the globe for extraordinary musicians, dancers, and circus performers who possess that rare, undeniable spark of world-class artistry.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1671966/img/talent_scouting_stage.png"
}, {
  title: "Creative Curation",
  description: "Our management team refines the act, tailoring every movement and note to meet the high-production standards of international luxury stages.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1671966/img/creative_direction_performance.png"
}, {
  title: "Global Logistics",
  description: "From visa coordination to technical riders, we handle the complex machinery of international touring so our stars can focus on the spotlight.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1671966/img/global_tour_management.png"
}, {
  title: "The Grand Premiere",
  description: "The curtain rises. We bring the world's most elite talent to prestigious venues, creating unforgettable moments of cinematic entertainment.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1671966/img/theatrical_stage_performance.png"
}];
function TimelineItem({
  step,
  index
}: {
  step: (typeof steps)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={index} className={cn("relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-0 items-center mb-20 md:mb-32 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16")}>
      {}
      <div className="absolute left-4 md:left-1/2 top-16 md:top-1/2 w-4 h-4 rounded-lg bg-primary -translate-x-1/2 md:-translate-y-1/2 z-10 ring-4 ring-background shadow-lg shadow-primary/20" />

      {}
      <div className={cn("pl-12 md:pl-0 flex justify-start", isEven ? "md:justify-end md:pr-16" : "md:justify-start md:pl-16 md:col-start-2 md:row-start-1")}>
        <img src={step.image} alt={step.title} className="w-32 h-32 md:w-80 md:h-80 rounded-lg object-cover shadow-2xl border-2 border-primary/20 grayscale hover:grayscale-0 transition-all duration-700" />
      </div>

      {}
      <div className={cn("pl-12 md:pl-0", isEven ? "md:pl-16" : "md:pr-16 md:col-start-1 md:row-start-1")}>
        <Card className="border-none shadow-2xl bg-card/40 backdrop-blur-md hover:bg-card/60 transition-all duration-500 relative overflow-hidden">
          <CardHeader className="relative z-10">
            <div className="text-6xl md:text-9xl font-serif font-bold text-primary/5 absolute -top-4 -left-4 pointer-events-none tracking-tighter">
              0{index + 1}
            </div>
            <div className="text-sm font-default font-bold text-primary mb-2 tracking-widest uppercase">
              Phase 0{index + 1}
            </div>
            <CardTitle className="text-2xl md:text-4xl font-default font-bold text-foreground tracking-tight">
              {step.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="relative z-10">
            <p className="text-muted-foreground leading-relaxed text-base md:text-lg font-default">
              {step.description}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function QualityJourney() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="223854" id="the-performance-journey" className="py-24 md:py-32 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {}
        <div ref={headerRef} className={cn("text-center max-w-3xl mx-auto mb-20 md:mb-32 transition-all duration-1000 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <h2 className="text-4xl md:text-7xl font-default font-bold text-foreground mb-6 tracking-tighter uppercase">
            The Performance Journey
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default tracking-wide">
            From the first spark of talent to the final standing ovation, we manage every detail of the global stage experience.
          </p>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-primary/0 via-primary/40 to-primary/0 md:-translate-x-1/2" />

          {}
          <div className="relative z-10 pt-8">
            {steps.map((step, index) => <TimelineItem key={index} step={step} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}